import axios from 'axios';
import API_CONFIG from '../config/api';

/**
 * Credits Service - API Integration
 * Handles all credits-related operations with the backend API
 */

/**
 * Add credits to user account
 * @param {number} amount - Amount of credits to add (25, 50, or 100)
 * @returns {Promise<Object>} Credit addition result
 */
export const addCredits = async (amount) => {
  try {
    const response = await axios.post(`${API_CONFIG.API_URL}/credits/add`, {
      amount: amount
    });
    
    // Return the actual API response structure
    return response.data;
  } catch (error) {
    console.error('Error adding credits:', error);
    
    // Handle different error scenarios
    if (error.response?.data) {
      throw new Error(error.response.data.detail || error.response.data.error || 'Failed to add credits');
    } else if (error.request) {
      throw new Error('Network error - please check your connection');
    } else {
      throw new Error('Failed to add credits');
    }
  }
};

/**
 * Get user credit balance
 * @returns {Promise<Object>} User credit balance and transaction history
 */
export const getCreditBalance = async () => {
  try {
    const response = await axios.get(`${API_CONFIG.API_URL}/credits/balance`);
    
    // Return the actual API response structure
    return response.data;
  } catch (error) {
    console.error('Error fetching credit balance:', error);
    
    // Handle different error scenarios
    if (error.response?.data) {
      throw new Error(error.response.data.detail || error.response.data.error || 'Failed to fetch credit balance');
    } else if (error.request) {
      throw new Error('Network error - please check your connection');
    } else {
      throw new Error('Failed to fetch credit balance');
    }
  }
};

/**
 * Get credit statistics
 * @returns {Promise<Object>} Credit statistics
 */
export const getCreditStatistics = async () => {
  try {
    const response = await axios.get(`${API_CONFIG.API_URL}/credits/statistics`);
    return response.data;
  } catch (error) {
    console.error('Error fetching credit statistics:', error);
    
    if (error.response?.data) {
      throw new Error(error.response.data.detail || error.response.data.error || 'Failed to fetch credit statistics');
    } else if (error.request) {
      throw new Error('Network error - please check your connection');
    } else {
      throw new Error('Failed to fetch credit statistics');
    }
  }
};

/**
 * Get valid credit amounts
 * @returns {Promise<Array>} Array of valid credit amounts
 */
export const getValidCreditAmounts = async () => {
  try {
    const response = await axios.get(`${API_CONFIG.API_URL}/credits/valid-amounts`);
    
    // Handle the API response structure: {"valid_amounts":[25,50,100],"message":"Valid credit amounts for addition"}
    if (response.data.valid_amounts) {
      return response.data.valid_amounts;
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching valid credit amounts:', error);
    
    if (error.response?.data) {
      throw new Error(error.response.data.detail || error.response.data.error || 'Failed to fetch valid credit amounts');
    } else if (error.request) {
      throw new Error('Network error - please check your connection');
    } else {
      throw new Error('Failed to fetch valid credit amounts');
    }
  }
};

/**
 * Check credits health
 * @returns {Promise<Object>} Credits system health status
 */
export const checkCreditsHealth = async () => {
  try {
    const response = await axios.get(`${API_CONFIG.API_URL}/credits/health`);
    return response.data;
  } catch (error) {
    console.error('Error checking credits health:', error);
    
    if (error.response?.data) {
      throw new Error(error.response.data.detail || error.response.data.error || 'Failed to check credits health');
    } else if (error.request) {
      throw new Error('Network error - please check your connection');
    } else {
      throw new Error('Failed to check credits health');
    }
  }
};

// Export all functions as default object for easier importing
const creditsService = {
  addCredits,
  getCreditBalance,
  getCreditStatistics,
  getValidCreditAmounts,
  checkCreditsHealth
};

export default creditsService;